import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, map, Observable, withLatestFrom } from 'rxjs';
import {
  loadUserWheels,
  selectActiveWheel,
  selectMode,
  selectPersonalWheelsLoading,
  selectShouldShowEmptyState,
  selectUserWheels,
  setActiveWheel,
  setMode,
} from '../../state';
import { WheelComponent } from '../wheel/wheel.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { selectIsAuthenticatedAndVerified, selectPlan, selectUserId } from 'src/app/state/auth/auth.selectors';
import { NEW_WHEEL_NAME } from '../wheel/features/wheel-name-form/wheel-name-form.component';
import { EmptyStateComponent } from '../wheel/ui/empty-state/empty-state.component';
import { mapActiveWheel } from '../../utils/mappers/active-wheel.mapper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { VerifyEmailPageComponent } from './ui/verify-email/verify-email-page.component';

@Component({
  selector: 'wil-personal',
  standalone: true,
  imports: [CommonModule, WheelComponent, FormsModule, ReactiveFormsModule, EmptyStateComponent, VerifyEmailPageComponent],
  templateUrl: './personal.component.html',
  styleUrl: './personal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PersonalComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  public readonly plan$: Observable<PlanType> = this.store
    .select(selectPlan)
    .pipe(map((plan) => (plan === 'personal' ? 'personal' : 'free')));

  public readonly isLoading$: Observable<boolean> = this.store.select(selectPersonalWheelsLoading);

  public readonly userId$ = this.store.select(selectUserId);
  public readonly shouldShowEmptyState$: Observable<boolean> = this.store.select(selectShouldShowEmptyState);
  public readonly isAuthenticatedAndVerified$: Observable<boolean> = this.store.select(selectIsAuthenticatedAndVerified);
  private readonly mode$: Observable<'view' | 'edit'> = this.store.select(selectMode);

  public activeWheelId$: Observable<string | null | undefined> = this.store.select(selectActiveWheel).pipe(
    filter(Boolean),
    distinctUntilChanged((previous, current) => previous.id === current.id),
    map((activeWheel) => activeWheel.id)
  );

  ngOnInit(): void {
    // fetched wheels from db && set active wheel && set mode
    this.store
      .select(selectUserWheels)
      .pipe(
        filter((w) => Boolean(w.length)),
        map((wheels) => mapActiveWheel(wheels)),
        withLatestFrom(this.mode$),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([mappedWheel, mode]) => {
        if (!mappedWheel) return;

        this.store.dispatch(setActiveWheel({ wheel: mappedWheel }));
        this.store.dispatch(setMode({ mode: mappedWheel.name === NEW_WHEEL_NAME || mode === 'edit' ? 'edit' : 'view' }));
      });

    this.store.dispatch(loadUserWheels());
  }
}
